// Did an archive request actually archive anything?
//
// Shared rather than inlined at each caller because the surfaces that archive
// without asking first now own their own success/failure reporting, and this is the
// part that is easy to get wrong: the outcome has to come from the resolved result
// of the request itself, never from `state.records.archiveErrorMessage` read out of
// props after the await. That slice is shared with any concurrent archive, and a
// component's props are frozen at their last render, so reading it back reports the
// wrong outcome in both directions.
//
// `crud` resolves `{ error: true }` on a non-2xx (actions/helpers/crud.js).
//
// The zero case is the subtle one: both archive endpoints scope by
// organization/department and silently drop tokens that do not match, yet still
// return 200 when NOTHING matched (records_controller.rb:799,
// receivers_controller.rb:79-90). So a 200 whose `archived_records` list is empty
// archived nothing and must be reported as a failure, not announced as success.
// A response carrying no list at all is left alone — absent evidence is not
// evidence of failure.
export const ARCHIVE_FAILURE_MESSAGE = 'We were unable to archive the selected records. Please try again';

export const archiveFailed = (result) => {
  if (result?.error) return true;

  const archivedTokens = result?.data?.archived_records;
  return Array.isArray(archivedTokens) && archivedTokens.length === 0;
};

// The unarchive half of the same question, kept separate for one reason: the two
// unarchive endpoints do not agree on what to call the list they echo back.
//
//   PATCH /api/v1/records/unarchive   -> { unarchived_tokens: [...] }
//                                        (records_controller.rb `unarchive`)
//   PATCH /api/v1/receivers/unarchive -> { archived_records: [...] }
//                                        (receivers_controller.rb `unarchive`)
//
// The receivers route reuses the *archive* key name for its unarchived tokens, so
// `archiveFailed` happens to catch the zero case there while silently missing it on
// the uploads route — a success toast over a record that never moved. Checking both
// keys is what makes this correct for either endpoint; renaming the payloads to
// agree would be the better fix but is a breaking API change, so it is deliberately
// not attempted here.
//
// Same zero-row reasoning as archive: both endpoints scope by
// organization/department, silently drop tokens that do not match, and still return
// 200 when nothing matched. An empty list means nothing was unarchived. A response
// carrying no list at all is left alone — absent evidence is not evidence of failure.
export const UNARCHIVE_FAILURE_MESSAGE = 'We were unable to unarchive the selected records. Please try again';
export const UNARCHIVE_RECORD_FAILURE_MESSAGE = 'We were unable to unarchive the record. Please try again';

export const unarchiveFailed = (result) => {
  if (result?.error) return true;

  const unarchivedTokens = result?.data?.unarchived_tokens ?? result?.data?.archived_records;
  return Array.isArray(unarchivedTokens) && unarchivedTokens.length === 0;
};

export default archiveFailed;
